import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import InfoGrid, {
  type InfoGridProps,
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { CUSTOMER_VIEW_TAB_PARAM } from "@/v2-backoffice-client/customer/customer.links"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import CustomerPaymentBadge from "@/v2-backoffice-client/customer/billing/CustomerPaymentBadge"
import CustomerValueBadge from "@/v2-backoffice-client/customer/billing/CustomerValueBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import CustomerLicenseBadge from "@/v2-backoffice-client/customer/license/CustomerLicenseBadge"
import { deriveCustomerSeatFacts } from "@/v2-backoffice-client/customer/license/customer.license.utils"
import { describeAccessSummary } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"
import LicenseChip from "@/v2-backoffice-client/license/LicenseChip"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import { deriveScopeUsageSignals } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"

// The grid renders the customer plus the license status display names merged
// in so they render as ordinary grid fields. LicenseRefs carries the
// customer's license rows (name, status, which catalog license) so the
// Licenses chips can say each license's status and deep-link to its panel —
// the customer row itself only has the joined names string.
/** One of the customer's licenses, as the header chips need it. */
export type CustomerLicenseRef = {
  licenseId: number
  /** The catalog license it is, when it has one; the panel link. */
  planId: number | null
  /** The catalog license's name, else the product's. */
  name: string
  /** The product, for the chip's hue and glyph. */
  productName: string
  license: {
    LicenseStatusCode: number
    TrialEndsAt?: string | null
    CancelledAt?: string | null
  }
}

type CustomerViewInfoData = CustomerFragment & {
  LicenseStatusName?: string
  LicenseRefs?: CustomerLicenseRef[]
  StatusNamesByCode?: Map<number, string>
  /** The customer's add-ons (id, name, whether a live license carries it), for the Add-on(s) chips. */
  AddOnRefs?: { id: number; name: string; isLive: boolean }[]
  /** What reaches the customer's users, for the Access line. */
  FeatureAccess?: CustomerFeatureAccessFragment
}

const FIELDS = createInfoGridFields<CustomerViewInfoData>({
  fields: {
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
    ExternalCustomerId: {
      label: "Hubspot Id",
      accessor: "ExternalCustomerId",
      to: ({ value }) => `${HUBSPOT_BASE_URL}/${value}`,
      defaultHidden: true,
    },
    // The Fenerum sibling of Hubspot Id: the linked billing account's uuid,
    // linking to its Fenerum page. Hidden (like Hubspot Id) — the toolbar's
    // Fenerum button is the everyday entry; this is the raw handle.
    BillingAccountUuid: {
      label: "Fenerum Id",
      accessor: "BillingAccountUuid",
      to: ({ value }) => `${FENERUM_ACCOUNT_BASE_URL}/${value}/`,
      valueClassName: "truncate",
      defaultHidden: true,
    },
    // What the customer is on: one chip per license — the name (the
    // catalog license's, or the product's when it has none) in the
    // product's hue, fused to that license's status (trial countdown and
    // overdue escalation included). The name links to the license's panel
    // (push: breadcrumb back). Until the license rows have loaded, the
    // governing status badge the boards show, which also says "No license"
    // for a customer without one. Managed on the panel's Licensing tab.
    Licenses: {
      label: "Licenses",
      accessor: "LicenseStatusCode",
      renderValue({ data }) {
        if (data.LicenseRefs?.length) {
          return (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {data.LicenseRefs.map((licenseRef) => (
                <LicenseChip
                  key={licenseRef.licenseId}
                  name={licenseRef.name}
                  productName={licenseRef.productName}
                  planId={licenseRef.planId}
                  license={licenseRef.license}
                  statusName={data.StatusNamesByCode?.get(
                    licenseRef.license.LicenseStatusCode,
                  )}
                />
              ))}
            </span>
          )
        }
        return (
          <LicenseGoverningBadge
            license={data}
            statusName={data.LicenseStatusName}
          />
        )
      },
    },
    // The add-ons the customer holds, beside the licenses they sit on top
    // of: one linked chip each (push: the add-on panel opens with a
    // breadcrumb back), amber when no live license carries it. Hidden
    // until loaded and when there are none, so a customer without add-ons
    // does not carry an empty field.
    AddOnRefs: {
      label: "Add-on(s)",
      accessor: (data) =>
        data.AddOnRefs?.map((addOnRef) => addOnRef.name).join(", ") ?? null,
      show: (data) => Boolean(data.AddOnRefs?.length),
      renderValue({ data }) {
        if (!data.AddOnRefs?.length) return
        return (
          <span className="flex flex-wrap gap-1">
            {data.AddOnRefs.map((addOnRef) => (
              <Link
                key={addOnRef.id}
                variant="muted"
                iconSrc={null}
                to={`>>?v=addOn:${addOnRef.id}`}
              >
                <Badge
                  size="sm"
                  variant={addOnRef.isLive ? "outline" : "warning"}
                  tooltip={
                    addOnRef.isLive
                      ? undefined
                      : "No live license carries this add-on: it gives nothing right now. See the Licensing tab."
                  }
                >
                  {addOnRef.name}
                </Badge>
              </Link>
            ))}
          </span>
        )
      },
    },
    // What reaches the customer's users, in one line, beside what they
    // bought: "Everyone's access" for most customers; for one that takes
    // only what is listed, the closed classes and the size of its own list,
    // so a CSM sees a narrowed customer before the customer calls. Links to
    // the What they get tab, where it is read in full and changed. Hidden
    // until the access row has loaded.
    Access: {
      label: "Access",
      accessor: (data) =>
        data.FeatureAccess ? describeAccessSummary(data.FeatureAccess) : null,
      renderValue({ data }) {
        if (!data.FeatureAccess) return
        const summary = describeAccessSummary(data.FeatureAccess)
        const isNarrowed = data.FeatureAccess.ClosedFeatureTypeCodes.length > 0
        return (
          <Link
            variant="muted"
            iconSrc={null}
            to={`?${CUSTOMER_VIEW_TAB_PARAM}=feature-access`}
          >
            {isNarrowed ? (
              <Badge size="sm" variant="warning">
                {summary}
              </Badge>
            ) : (
              summary
            )}
          </Link>
        )
      },
    },
    // Next renewal among licenses that will bill again (lic.Licenses) —
    // the date the renewal insight counts down to.
    LicenseRenewsAt: {
      label: "Renews",
      accessor: "LicenseRenewsAt",
      show: (data) => Boolean(data.LicenseRenewsAt),
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    Stage: {
      label: "Stage",
      accessor: "Stage",
      renderValue({ value }) {
        if (!value) return
        return <Badge size="sm">{String(value)}</Badge>
      },
    },
    // The HubSpot company owner — whose CRM book this account belongs to,
    // so a panel answers "who owns this" without a HubSpot round-trip.
    // Hidden when the CRM sync has no owner for the company.
    OwnerName: {
      label: "Company Owner",
      accessor: "OwnerName",
      valueClassName: "truncate",
      show: (data) => Boolean(data.OwnerName),
    },
    // The four health stats promoted from the Activity and Billing tabs —
    // same badges as the boards and the tabs (one derivation, one
    // presentation), so the panel header answers "how is this account
    // doing" before any tab is opened. Each hides when its source has no
    // data (no tracked usage / no invoice history).
    ChurnRisk: {
      label: "Churn Risk",
      // The derived level, not a raw column: null exactly when the badge
      // would be silent (no seats), so the field hides with it.
      accessor: (data) => {
        const level = deriveScopeUsageSignals(data)?.churnRisk.level
        return level && level !== "none" ? level : null
      },
      renderValue({ data }) {
        const signals = deriveScopeUsageSignals(data)
        if (!signals || signals.churnRisk.level === "none") return
        const { level, reasons } = signals.churnRisk
        // The money rides only on at-risk badges — same rule as the
        // boards' churn column (usage.columns.tsx).
        const atStake =
          (level === "high" || level === "medium") &&
          data.Billed12M != null &&
          data.Billed12M > 0
            ? `${formatCompactAmount(data.Billed12M, data.PrimaryCurrency)} billed the last 12 months rides on this account.`
            : null
        return (
          <UsageChurnRiskBadge
            level={level}
            reasons={atStake ? [...reasons, atStake] : reasons}
          />
        )
      },
    },
    Engagement: {
      label: "Engagement",
      accessor: (data) => deriveScopeUsageSignals(data)?.avgScore ?? null,
      renderValue({ data }) {
        const avgScore = deriveScopeUsageSignals(data)?.avgScore
        if (avgScore == null) return
        return <UsageScoreBadge score={avgScore} />
      },
    },
    CustomerValue: {
      label: "Customer Value",
      accessor: "ValueTier",
      renderValue({ data }) {
        return <CustomerValueBadge customer={data} />
      },
    },
    PaymentHealth: {
      label: "Payment Health",
      accessor: "PaymentHealth",
      renderValue({ data }) {
        return <CustomerPaymentBadge customer={data} />
      },
    },
    OrganizationNumber: {
      label: "Org Number",
      accessor: "OrganizationNumber",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    Email: {
      label: "Email",
      accessor: "Email",
      valueClassName: "truncate",
    },
    Phone: {
      label: "Phone",
      accessor: "Phone",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    Website: {
      label: "Website",
      accessor: "Website",
      valueClassName: "truncate",
      defaultHidden: true,
      renderValue: ({ data }) => {
        if (!data.Website || !data.Domain) return
        return (
          <a
            href={data.Website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {data.Domain || data.Website}
          </a>
        )
      },
    },
    Type: {
      label: "Type",
      accessor: "Type",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    PrimarySegment: {
      label: "Primary Segment",
      accessor: "PrimarySegment",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    SecondarySegment: {
      label: "Secondary Segment",
      accessor: "SecondarySegment",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    LicenseSeats: {
      // Occupied seats (users + open invitations) against the seat caps of
      // the customer's live lic.Licenses rows.
      label: "License Seats",
      // The derived seat status, not a raw column: the grid's hide-empty
      // pass keys on this accessor value, and the status is null exactly
      // when the badge renders nothing (no license held), so the field
      // hides exactly when the badge would be silent.
      accessor: (data) => deriveCustomerSeatFacts(data).status,
      renderValue({ data }) {
        return <CustomerLicenseBadge customer={data} />
      },
    },
    // Fusion-login migration progress across every user of the customer
    // (FUS-927) — same component and buckets as the boards' columns.
    LoginMigration: {
      label: "Login Migration",
      accessor: (data) => data.MigrationTotal ?? null,
      renderValue({ data }) {
        return <MigrationProgress rollup={data} className="max-w-56" />
      },
    },
    TotalUsers: {
      label: "Total Users",
      accessor: "UserCount",
    },
    ActiveUsers: {
      label: "Active Users",
      accessor: "ActiveUserCount",
    },
    InactiveUsers: {
      label: "Inactive Users",
      accessor: "InactiveUserCount",
    },
    CreatedAt: {
      label: "Created",
      accessor: "CreatedAt",
      valueClassName: "truncate",
      renderValue: ({ value }) => {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    UpdatedAt: {
      label: "Updated",
      accessor: "UpdatedAt",
      valueClassName: "truncate",
      defaultHidden: true,
      renderValue: ({ value }) => {
        if (!value) return
        return formatDateString(String(value))
      },
    },
  },
})

export type CustomerViewInfoProps = Omit<
  InfoGridProps<CustomerViewInfoData>,
  "data" | "fields"
> & {
  customer: CustomerFragment | null | undefined
  /** The customer's license rows, for the Licenses chips (name, status, panel link). */
  licenseRefs?: CustomerLicenseRef[]
  /** The customer's add-ons — the Add-on(s) chips, linked to the add-on panel. */
  addOnRefs?: { id: number; name: string; isLive: boolean }[]
  /** The customer's access as one read — the Access line. */
  featureAccess?: CustomerFeatureAccessFragment
}

function CustomerViewInfo(props: CustomerViewInfoProps) {
  const {
    customer,
    licenseRefs,
    addOnRefs,
    featureAccess,
    className,
    ...infoGridProps
  } = props

  // Status display names for the license badges, from the seeded lookup the
  // boards also use (cached, so this is free after the first load).
  const { data: licenseStatusData } = useLicenseStatusesQuery({
    skip: customer?.LicenseStatusCode == null,
  })
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (licenseStatusData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [licenseStatusData?.getLicenseStatuses],
  )

  const gridData: CustomerViewInfoData | null | undefined = customer
    ? {
        ...customer,
        LicenseStatusName: statusNamesByCode.get(
          customer.LicenseStatusCode ?? -1,
        ),
        LicenseRefs: licenseRefs,
        StatusNamesByCode: statusNamesByCode,
        AddOnRefs: addOnRefs,
        FeatureAccess: featureAccess,
      }
    : customer

  return (
    <InfoGrid
      id="customer-info"
      className={cn("customer-view-info", className)}
      data={gridData}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default CustomerViewInfo
